import { Modal, Table } from '@getsynapse/design-system';
import { ModalProps } from '@getsynapse/design-system/dist/Organisms/Modal/Modal';
import intl from 'react-intl-universal';
import React, { useEffect } from 'react';
import useBrokenBudgetOptions from '../hooks/useBrokenBudgetOptions';
import classnames from 'classnames';
import {
  LineItemWithAmounts,
  UpdateFixedBrokenLineItemsRequest,
} from '../../../utils/customTypes';
import useBudgetFilters from '../hooks/useBudgetFilters';
import {
  setShowBrokenModal,
  updateFixedBrokenLineItems,
} from '../../../state/BudgetList/budgetListSlice';
import { useDispatch } from 'react-redux';
import { generateBrokenLineItemRows } from '../helpers/budgetRowHelper';

export type BrokenRelationshipModalProps = ModalProps & {
  onCancel: () => void;
};

export const BrokenRelationshipModal = ({
  onCancel,
  ...props
}: BrokenRelationshipModalProps) => {
  const [haveChanged, setHaveChanged] = React.useState(false);
  const dispatch = useDispatch();
  const [selectedProgramIds, setSelectedProgramIds] = React.useState<
    Record<string, string | null>
  >({});

  const [fixedLineItems, setFixedLineItems] = React.useState<
    Array<LineItemWithAmounts>
  >([]);

  const handleProgramChange = (lineItemId: string, programId: string) => {
    setSelectedProgramIds((prev) => ({
      ...prev,
      [lineItemId]: programId,
    }));
  };

  const { projectToProgramMap, brokenLineItems } = useBrokenBudgetOptions();

  useEffect(() => {
    setFixedLineItems([...brokenLineItems]);
    const initialProgramIds = brokenLineItems.reduce((acc, lineItem) => {
      acc[lineItem.id] = lineItem.programId || null;
      return acc;
    }, {} as Record<string, string | null>);

    setSelectedProgramIds(initialProgramIds);
  }, [brokenLineItems]);

  const { learningTeamOptions, programOptions, projectOptions } =
    useBudgetFilters();

  async function onEditLineItem(
    lineItem: string,
    field?: string,
    value?: string
  ) {
    setFixedLineItems((prevItems) =>
      prevItems.map((item) =>
        item.id === lineItem ? { ...item, [field as string]: value } : item
      )
    );
  }

  const onSaveChanges = () => {
    dispatch(setShowBrokenModal(false));
    const updatedFixedLineItems: UpdateFixedBrokenLineItemsRequest[] =
      fixedLineItems.map((lineItem) => ({
        id: lineItem.id,
        learningTeamId: lineItem.learningTeamId,
        programId: lineItem.programId,
        projectId: lineItem.projectId,
      }));

    dispatch(updateFixedBrokenLineItems(updatedFixedLineItems));
  };

  const applyToAll = (
    updatedProgramId: string,
    matchingLineItems: Array<LineItemWithAmounts>
  ) => {
    setFixedLineItems((prevFixedLineItems) => {
      const updatedItems = matchingLineItems.map((lineItem) => {
        return { ...lineItem, programId: updatedProgramId };
      });

      const updatedSet = [...prevFixedLineItems];
      updatedItems.forEach((updatedItem) => {
        const existingIndex = updatedSet.findIndex(
          (item) => item.id === updatedItem.id
        );
        if (existingIndex > -1) {
          updatedSet[existingIndex] = updatedItem;
        } else {
          updatedSet.push(updatedItem);
        }
      });

      return updatedSet;
    });

    const updatedSelectedProgramIds = { ...selectedProgramIds };
    matchingLineItems.forEach((lineItem) => {
      updatedSelectedProgramIds[lineItem.id] = updatedProgramId;
    });
    setSelectedProgramIds(updatedSelectedProgramIds);
  };

  function onRemindMeLater() {
    dispatch(setShowBrokenModal(false));
    if (!haveChanged) {
      setHaveChanged(true);
    }
    onCancel();
  }

  return (
    <Modal
      {...props}
      aria-label={intl.get('BUDGET_PAGE.BROKEN_RELATION_MODAL.TITLE')}
      titleIcon={{
        name: 'information-circle',
        className: 'pointer-events-none',
      }}
      title={intl.get('BUDGET_PAGE.BROKEN_RELATION_MODAL.TITLE')}
      size='large'
      childrenClassName='pr-0'
      actionButtons={[
        {
          children: intl.get('BUDGET_PAGE.BROKEN_RELATION_MODAL.CONFIRM'),
          onClick: onSaveChanges,
          variant: 'primary',
          hasDisabledStyle: !haveChanged,
          'data-cy': 'remove-line-item_button',
        },
        {
          children: intl.get('BUDGET_PAGE.BROKEN_RELATION_MODAL.REMIND_ME'),
          variant: 'tertiary',
          onClick: onRemindMeLater,
          'data-cy': 'remind-me-later_button',
        },
      ]}
    >
      <div>
        <div className={'mb-3'}>
          {intl.get('BUDGET_PAGE.BROKEN_RELATION_MODAL.DESCRIPTION')}
        </div>
        <Table
          canSelectRows={false}
          className='relative max-w-full border-0'
          data={{
            total: 0,
            headData: {
              stickyHeader: true,
              headCells: [
                {
                  content: intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.NAME'),
                  className: classnames('h-10 w-44 left-0 z-2'),
                },

                {
                  content: intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.TEAM'),
                  className: classnames('w-32'),
                },
                {
                  content: intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.PROGRAM'),
                  className: classnames('w-32'),
                },
                {
                  content: intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.PROJECT'),
                  className: classnames('w-32'),
                },
                {
                  'data-cy': 'table-header__budget-annual',
                  content: 'Actions',
                  className: classnames('w-28'),
                },
              ].filter(Boolean),
            },
            rows: generateBrokenLineItemRows(
              fixedLineItems || [],
              onEditLineItem,
              programOptions,
              projectToProgramMap,
              learningTeamOptions,
              projectOptions,
              selectedProgramIds,
              handleProgramChange,
              applyToAll,
              setHaveChanged
            ).flat(),
          }}
        />
      </div>
    </Modal>
  );
};
